import relativeTime from 'dayjs/plugin/relativeTime';
import React, { FunctionComponent, useEffect, useLayoutEffect, useRef } from 'react';
import { CanAccess, useTranslate } from '@pankod/refine-core';
import { Button, Divider, Group, HoverCard, ScrollArea, Skeleton, Stack, Title } from '@pankod/refine-mantine';
import { ActionIcon, Center, Flex, Kbd, Loader, Transition } from '@mantine/core';
import { DetailsGrid } from '../../../molecules/DetailsGrid/DetailsGrid';
import { WarningAlert } from '../../../components/atoms/WarningAlert/WarningAlert';
import { DataField } from '../../../components/molecules/DataField/DataField';
import routerProvider from '@pankod/refine-react-router-v6';
import { EState } from '../../../mock-service-worker/users/enums';
import dayjs from 'dayjs';
import * as faceapi from 'face-api.js';
import { BallerineImage } from '../../../components/atoms/BallerineImage/BallerineImage';
import {
  useApproveUserMutation,
  useHandleSelectedUser,
  useMockData,
  useRejectUserMutation,
  useSelectedUserQuery,
  useUserQuery,
} from '../hooks';
import { IconDotsVertical } from '@tabler/icons';
import { formatDate, isValidDate } from 'utils';
import { SubjectImageViewer } from './SubjectImageViewer';
import { createWorker } from 'tesseract.js';

const worker = createWorker();
const ocrInitPromise = async () => {
  await worker.load();
  await worker.loadLanguage('eng');
  await worker.initialize('eng');
};
// For the fromNow method.
dayjs.extend(relativeTime);

// const faceapiInitPromise = faceapi.loadFaceRecognitionModel('https://justadudewhohacks.github.io/face-api.js/models');

export interface ISubjectContentProps {
  nextId: string;
}

export const isTwoDimensionalArray = (
  array: Array<any> | Float32Array,
): array is Array<Array<any>> | Array<Float32Array> => array.every(Array.isArray);

export const SubjectContent: FunctionComponent<ISubjectContentProps> = ({ nextId }) => {
  const t = useTranslate();
  const [faceSimilarity, setFaceSimilarity] = React.useState(0);
  const [contentMounted, setContentMounted] = React.useState(false);
  const [ocrText, setOcrText] = React.useState<string>('');
  const { selectUser } = useHandleSelectedUser();
  const { data: selectedUser, isLoading: isLoadingSelectedUser } = useSelectedUserQuery();
  // eslint-disable-next-line @typescript-eslint/no-unsafe-call,@typescript-eslint/no-unsafe-assignment
  const { id = '' } = routerProvider.useParams() as {
    id: string;
  };
  const { isFetching, isLoading } = useUserQuery(id);
  const { personalDetails, passportDetails, checkResults, addressDetails, images } = useMockData();
  const selfieRef = useRef<HTMLImageElement | null>(null);
  const docFaceRef = useRef<HTMLImageElement | null>(null);
  // For OCR.
  const licenseFront = images?.find(({ docType }) => docType === 'license front')?.url;
  const { mutateAsync: mutateAsyncReject, isLoading: isLoadingRejectUserMutation } = useRejectUserMutation();
  const { mutateAsync: mutateAsyncApprove, isLoading: isLoadingApproveUserMutation } = useApproveUserMutation();
  const onRejectUser = async () => {
    return await mutateAsyncReject(
      {
        resource: 'users',
        id: selectedUser?.data?.id ?? '',
        values: {
          state: EState.REJECTED,
          first_name: personalDetails.firstName,
          last_name: personalDetails.lastName,
        },
      },
      {
        onSuccess() {
          if (!nextId) return;

          selectUser(nextId)();
        },
      },
    );
  };
  const onApproveUser = async () => {
    return await mutateAsyncApprove(
      {
        resource: 'users',
        id: selectedUser?.data?.id ?? '',
        values: {
          state: EState.APPROVED,
          first_name: personalDetails.firstName,
          last_name: personalDetails.lastName,
        },
      },
      {
        onSuccess() {
          if (!nextId) return;

          selectUser(nextId)();
        },
      },
    );
  };

  useLayoutEffect(() => {
    void (async () => {
      console.log('calculating face similarity');
      return;
//      try {
//        await faceapiInitPromise;
//
//        if (!selfieRef.current || !docFaceRef.current) return;
//
//        const [selfieDesc, documentFacePhotoDesc] = await Promise.all([
//          faceapi.computeFaceDescriptor(selfieRef.current),
//          faceapi.computeFaceDescriptor(docFaceRef.current),
//        ]);
//
//        console.log('face similarity calculated', documentFacePhotoDesc, selfieDesc);
//
//        // Both elements can be Float32Array | Float32Array[] when faceapi.euclideanDistance expects Float32Array | number[].
//        if (isTwoDimensionalArray(documentFacePhotoDesc) || isTwoDimensionalArray(selfieDesc)) {
//          console.error('Two dimensional arrays are not supported.');
//
//          return;
//        }
//
//        let distance = faceapi.utils.round(faceapi.euclideanDistance(documentFacePhotoDesc, selfieDesc));
//
//        distance = Math.round((1 - distance) * 100 * 1.5);
//        distance = distance > 100 ? 100 : distance;
//        console.log('distance', distance);
//        setFaceSimilarity(distance);
//      } catch (err) {
//        console.error(err);
//      }
    })();
  }, [selfieRef.current, docFaceRef.current, selectedUser?.data?.id]);

  useEffect(() => {
    void (async () => {
      console.log('OCR calculation');
      await ocrInitPromise();

      if (!images[0]?.url) return;

      const { data } = await worker.recognize(images[0].url);
      setOcrText(data.text);
    })();
  }, [images[0]?.url]);

  useEffect(() => {
    setContentMounted(!isFetching);
  }, [isFetching]);

  if (!id) return null;
  const shiftRight = {
    in: { opacity: 1, transform: 'translateX(0)' },
    out: { opacity: 0, transform: 'translateX(-50px)' },
    transitionProperty: 'transform, opacity',
  };
  const shiftTop = {
    in: { opacity: 1, transform: 'translateY(0)' },
    out: { opacity: 0, transform: 'translateY(50px)' },
    transitionProperty: 'transform, opacity',
  };
  return (
    <div
      style={{
        flexGrow: 1,
        height: '100%',
        boxShadow: '0px 4px 39px rgba(99, 117, 165, 0.1)',
        padding: '10px',
      }}
    >
      <Group style={{ padding: 10 }} position="apart">
        <Title order={2} sx={{ fontSize: '2rem', marginInlineStart: '2.4rem' }}>
          {isLoadingSelectedUser ? (
            <Skeleton
              sx={{
                height: '2.5rem',
                width: '13ch',
              }}
            />
          ) : (
            <Transition mounted={!isFetching} transition={shiftRight} duration={200} timingFunction="ease">
              {transitionStyles => <div style={transitionStyles}>{selectedUser?.data?.fullName}</div>}
            </Transition>
          )}
        </Title>
        <Group grow>
          <HoverCard shadow="md">
            <HoverCard.Target>
              <Button
                style={{ maxWidth: '170px', minWidth: '170px' }}
                variant="light"
                color="red"
                // Appeases ESLint at the expense of recreating the function on every render.
                // When trying onClick={void onRejectUser} the handler does not work.
                onClick={() => {
                  return void onRejectUser();
                }}
                loading={isLoadingRejectUserMutation}
              >
                Reject
              </Button>
            </HoverCard.Target>
            <HoverCard.Dropdown>
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <Kbd>Ctrl</Kbd>
                <span style={{ margin: '0 5px' }}>+</span>
                <Kbd>J</Kbd>
              </div>
            </HoverCard.Dropdown>
          </HoverCard>
          <HoverCard shadow="md">
            <HoverCard.Target>
              <Button
                style={{ maxWidth: '170px', minWidth: '170px' }}
                variant="light"
                color="green"
                onClick={() => {
                  return void onApproveUser();
                }}
                loading={isLoadingApproveUserMutation}
              >
                Approve
              </Button>
            </HoverCard.Target>
            <HoverCard.Dropdown>
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <Kbd>Ctrl</Kbd>
                <span style={{ margin: '0 5px' }}>+</span>
                <Kbd>A</Kbd>
              </div>
            </HoverCard.Dropdown>
          </HoverCard>

        </Group>
      </Group>
      {isFetching && (
        <Center style={{ padding: 50 }}>
          <Loader color="blue" size="md"></Loader>
        </Center>
      )}
      {!isFetching && (
        <ScrollArea
          sx={{
            // Header's height * 2.
            height: 'calc(100vh - 126px)',
          }}
        >
          <Transition mounted={contentMounted} transition={shiftTop} duration={200} timingFunction="ease">
            {transitionStyles => (
              <Group grow style={{ flex: 1, height: '100%', ...transitionStyles }}>
                <Stack
                  style={{
                    height: '100%',
                    padding: '3.125rem',
                  }}
                >
                  <Flex sx={{ columnGap: '1.5rem' }}>
                    <BallerineImage
                      ref={selfieRef}
                      alt={'User avatar 1'}
                      src={images?.find(({ docType }) => docType === 'Selfie')?.url ?? ''}
                      width={114}
                      height={143}
                      fit={'cover'}
                    />
                    <BallerineImage
                      ref={docFaceRef}
                      alt={'User avatar 2'}
                      src={images?.find(({ docType }) => docType === 'ID Document (Face)')?.url ?? ''}
                      width={114}
                      height={143}
                      fit={'cover'}
                    />
                  </Flex>
                  <Group style={{ marginTop: '1.5rem' }} position="left" spacing="xl">
                    <DataField
                      title={'Face Similarity'}
                      text={`${faceSimilarity}%`}
                      textProps={{ color: faceSimilarity > 80 ? 'green' : 'red' }}
                      sx={{
                        textTransform: 'capitalize',
                        color: faceSimilarity > 80 ? 'green' : 'red',
                        textColor: faceSimilarity > 60 ? 'green' : 'red',
                      }}
                    />
                    <DataField
                      title={'Image Quality'}
                      text={'OK'}
                      sx={{
                        textTransform: 'capitalize',
                        color: 'green',
                        textColor: 'green',
                      }}
                    />
                  </Group>
                  <Divider
                    sx={{
                      borderRadius: '49px',
                      borderColor: '#F2F2F2',
                    }}
                  />

                  <DetailsGrid
                    title={'Personal info'}
                    data={personalDetails}
                    Footer={
                      <WarningAlert>
                        <Flex
                          sx={{
                            justifyContent: 'space-between',
                            color: '#4D4D4D',
                          }}
                        >
                          OCR & Given details mismatch
                          <Button
                            variant={'outline'}
                            sx={{
                              height: 'unset',
                              color: '#3F77FF',
                              border: 'none',
                              fontWeight: 400,
                              fontSize: '0.875rem',
                              '&:hover': {
                                backgroundColor: 'transparent',
                                textDecoration: 'underline',
                              },
                            }}
                          >
                            Resolve
                          </Button>
                        </Flex>
                      </WarningAlert>
                    }
                  >
                    {({ text, title, ...rest }) => {
                      const value = isValidDate(text) ? formatDate(new Date(text)) : text;

                      return (
                        <DataField
                          title={title}
                          text={value}
                          sx={{
                            textTransform: 'capitalize',
                          }}
                          {...rest}
                        />
                      );
                    }}
                  </DetailsGrid>
                  <DetailsGrid title={'Passport info'} data={passportDetails}>
                    {({ text, title, ...rest }) => {
                      const value = isValidDate(text) ? formatDate(new Date(text)) : text;

                      return (
                        <DataField
                          title={title}
                          text={value}
                          sx={{
                            textTransform: 'capitalize',
                          }}
                          {...rest}
                        />
                      );
                    }}
                  </DetailsGrid>
                  <DetailsGrid
                    title={'Check results'}
                    data={checkResults}
                    Header={
                      <Button
                        variant={'outline'}
                        sx={{
                          color: '#3F77FF',
                          border: 'none',
                          fontWeight: 400,
                          fontSize: '0.875rem',
                          height: 'unset',
                          '&:hover': {
                            backgroundColor: 'transparent',
                            textDecoration: 'underline',
                          },
                        }}
                      >
                        View full report
                      </Button>
                    }
                  >
                    {({ text, title, ...rest }) => {
                      const color = (() => {
                        if (text === EState.APPROVED) {
                          return '#1ED400';
                        }

                        if (text === EState.REJECTED) {
                          return '#D40000FF';
                        }

                        return '';
                      })();

                      return (
                        <DataField
                          title={title.replace(/aml/i, 'AML')}
                          text={text}
                          textProps={{
                            sx: {
                              color,
                              textTransform: 'capitalize',
                            },
                          }}
                          {...rest}
                        />
                      );
                    }}
                  </DetailsGrid>
                  <DetailsGrid title={'Address'} data={addressDetails}>
                    {({ text, title, ...rest }) => (
                      <DataField
                        title={title.replace(/^apt/i, 'Apt.')}
                        text={text}
                        sx={{
                          textTransform: 'capitalize',
                        }}
                        {...rest}
                      />
                    )}
                  </DetailsGrid>
                </Stack>
                <Stack
                  style={{
                    height: '100%',
                    padding: '3.125rem',
                    alignSelf: 'flex-start',
                  }}
                >
                  <CanAccess resource={'image-viewer'} action={'show'}>
                    <SubjectImageViewer isLoading={isLoading} ocrText={ocrText} images={images} />
                  </CanAccess>
                </Stack>
              </Group>
            )}
          </Transition>
        </ScrollArea>
      )}
    </div>
  );
};
